import React from 'react';
import styled from 'styled-components';
import { useAmplitude } from 'shared/hooks/use-amplitude';

import useProductQueryParams from 'hooks/use-product-query-params';
import useTranslation from 'hooks/use-translation';

import Section from 'components/sidebar-filters/section';

// Click selects a band; click again clears back to all prices.
export default function PriceFilter({ bands }) {
  const { queryParams, defaults, setQueryParams } = useProductQueryParams();
  const selectedBandId = queryParams.pricerange;
  const amplitude = useAmplitude();
  const { t } = useTranslation();

  if (!bands?.length) {
    return null;
  }

  const handleClick = (bandId) => {
    const newValue = selectedBandId === bandId ? defaults.pricerange : bandId;

    amplitude.log(`Price Range Filter`, {
      description: `User clicks/taps on Price Range Filter while shopping`,
      priceRangeSelected: newValue,
    });

    setQueryParams({
      pricerange: newValue,
      page: defaults.page,
    });
  };

  return (
    <Section addPaddingRight startsOpen heading={t('common.price', 'Price')} data-testid='price-filter'>
      <ChipsContainer>
        {bands.map((band) => (
          <Chip
            key={band.bandId}
            type='button'
            selected={band.bandId === selectedBandId}
            onClick={() => handleClick(band.bandId)}
            data-testid={`price-filter-chip-${band.bandId}`}
          >
            {band.label}
          </Chip>
        ))}
      </ChipsContainer>
    </Section>
  );
}

// Grid (matching menu-sidebar/weight-filter's ButtonsContainer) rather than flex-wrap: chip
// widths follow their column, not their own label length, so short labels like "$50+" don't
// bunch up two-to-a-row next to lonely full-row chips like "Under $10" -- every chip lines up
// into a clean, consistently-sized 2-column grid regardless of band count or label length.
const ChipsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
`;

const Chip = styled.button`
  align-items: center;
  border-radius: var(--dct--customized-radius-sm, 16px);
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 12px;
  font-weight: ${({ selected }) => (selected ? `bold` : `normal`)};
  height: 34px;
  justify-content: center;
  outline: none;
  padding: 0;
  background: ${({ selected, theme }) =>
    selected
      ? `var(--dcm-color-surface-hover, ${theme.colors.grey[35]})`
      : `var(--dcm-filter-search-bg, var(--dcm-color-surface, ${theme.colors.white}))`};
  border: ${({ selected, theme }) =>
    selected ? `none` : `solid 1px var(--dcm-filter-border-color, ${theme.colors.blueGrey[80]})`};
  color: ${({ selected, theme }) =>
    selected ? theme.colors.white : `var(--dcm-filter-text-color, ${theme.colors.grey[25]})`};
`;
